import { request, requestText, streamSSE } from "@/lib/apiClient";

function paymentIdempotencyKey() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return `payment-${Date.now()}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
}

export async function fetchWorkspaceSnapshot(sessionId, tzOffsetMinutes = 60, cursor = "") {
  const params = new URLSearchParams({
    limit: "1000",
    tzOffsetMinutes: String(tzOffsetMinutes),
  });
  if (cursor) params.set("since", cursor);
  return request(`/api/v1/sync/pull?${params.toString()}`, {
    sessionId,
  });
}

export async function fetchSubscriptionStatus(sessionId) {
  return request("/api/v1/subscription/status?clientChannel=web", { sessionId });
}

export async function fetchGuidedTutorialProgress(sessionId) {
  const payload = await request("/api/v1/tutorial/progress", { sessionId });
  return payload?.progress;
}

export async function updateGuidedTutorialProgress(sessionId, { action, expectedStep }) {
  const payload = await request("/api/v1/tutorial/progress", {
    body: JSON.stringify({ action, expectedStep }),
    method: "POST",
    sessionId,
  });
  return payload?.progress;
}

export async function fetchBillingWallet(sessionId) {
  return request("/api/v1/wallet", { sessionId });
}

export async function initializeBillingWalletFunding(sessionId, { amountMinor, returnUrl }) {
	return request("/api/v1/wallet/funding/init", {
		body: JSON.stringify({ amountMinor, clientChannel: "web", returnUrl }),
		headers: { "Idempotency-Key": paymentIdempotencyKey() },
    method: "POST",
    sessionId,
  });
}

export async function confirmBillingWalletFunding(sessionId, reference) {
  return request("/api/v1/wallet/funding/confirm", {
    body: JSON.stringify({ clientChannel: "web", reference }),
    method: "POST",
    sessionId,
  });
}

export async function fetchSubscriptionPaymentReceipt(sessionId, reference) {
  return request(`/api/v1/subscription/payments/${encodeURIComponent(reference)}/receipt`, { sessionId });
}

export async function initializeSubscriptionCheckout(sessionId, { autoRenew = false, billingCycle = "monthly", planId, returnUrl }) {
  return request("/api/v1/subscription/checkout/init", {
		body: JSON.stringify({ autoRenew, billingCycle, clientChannel: "web", planId, provider: "squad", returnUrl }),
		headers: { "Idempotency-Key": paymentIdempotencyKey() },
    method: "POST",
    sessionId,
  });
}

export async function recordSubscriptionCheckoutClientState(sessionId, reference, state) {
  return request("/api/v1/subscription/checkout/client-state", {
    body: JSON.stringify({ provider: "squad", reference, state }),
    method: "POST",
    sessionId,
  });
}

export async function updateSubscriptionAutoRenewal(sessionId, enabled) {
  return request("/api/v1/subscription/auto-renewal", {
    body: JSON.stringify({ enabled }),
    method: "PATCH",
    sessionId,
  });
}

export async function confirmSubscriptionCheckout(sessionId, reference) {
  return request("/api/v1/subscription/checkout/confirm", {
    body: JSON.stringify({ provider: "squad", reference }),
    method: "POST",
    sessionId,
  });
}

export async function fetchMyReferrals(sessionId) {
  return request("/api/v1/referrals/me", { sessionId });
}

export async function fetchSupportCategories(sessionId) {
  return request("/api/v1/support/categories", { sessionId });
}

export async function fetchSupportTickets(sessionId) {
  return request("/api/v1/support/tickets", { sessionId });
}

export async function fetchSupportTicket(sessionId, ticketId) {
  return request(`/api/v1/support/tickets/${encodeURIComponent(ticketId)}`, { sessionId });
}

export async function createSupportTicket(sessionId, input) {
  return request("/api/v1/support/tickets", {
    body: JSON.stringify(input),
    method: "POST",
    sessionId,
  });
}

export async function replySupportTicket(sessionId, ticketId, body) {
  const idempotencyKey =
    (typeof crypto !== "undefined" && crypto.randomUUID?.()) ||
    `support-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return request(`/api/v1/support/tickets/${encodeURIComponent(ticketId)}/messages`, {
    body: JSON.stringify({ body }),
    headers: { "Idempotency-Key": idempotencyKey },
    method: "POST",
    sessionId,
  });
}

export async function fetchDashboard(sessionId, tzOffsetMinutes = 60) {
  return request(`/api/v1/dashboard?tzOffsetMinutes=${tzOffsetMinutes}`, { sessionId });
}

export async function fetchInventory(sessionId, scope = "all", query = "", tzOffsetMinutes = 60) {
  const params = new URLSearchParams();
  params.set("limit", "500");
  if (query) params.set("q", query);
  params.set("tzOffsetMinutes", String(tzOffsetMinutes));
  return request(`/api/v1/inventory/${scope}?${params.toString()}`, { sessionId });
}

export async function fetchSalesHistory(sessionId, filters = 250) {
  const params = new URLSearchParams();
  if (typeof filters === "number") {
    params.set("limit", String(filters));
  } else {
    params.set("limit", String(filters.limit ?? 250));
    if (filters.from) params.set("from", filters.from);
    if (filters.to) params.set("to", filters.to);
    if (filters.paymentStatus && filters.paymentStatus !== "all") params.set("paymentStatus", filters.paymentStatus);
    if (filters.cashier) params.set("cashier", filters.cashier);
  }
  return request(`/api/v1/sales/history?${params.toString()}`, { sessionId });
}

export async function exportSalesHistory(sessionId, filters = {}) {
  const params = new URLSearchParams();
  if (filters.from) params.set("from", filters.from);
  if (filters.to) params.set("to", filters.to);
  if (filters.paymentStatus && filters.paymentStatus !== "all") params.set("paymentStatus", filters.paymentStatus);
  if (filters.cashier) params.set("cashier", filters.cashier);
  return requestText(`/api/v1/sales/history/export?${params.toString()}`, { sessionId });
}

export async function fetchProductCatalog(sessionId, query = "", limit = 25) {
  const params = new URLSearchParams();
  params.set("limit", String(limit));
  if (query) params.set("q", query);
  return request(`/api/v1/products/catalog?${params.toString()}`, { sessionId });
}

export async function fetchGlobalProductCatalog(sessionId, { limit = 1000, since = "" } = {}) {
  const params = new URLSearchParams({ limit: String(limit) });
  if (since) params.set("since", since);
  const payload = await request(`/api/v1/products/catalog?${params.toString()}`, { sessionId });
  return payload.catalog ?? { nextCursor: since, products: [] };
}

export async function fetchCreditBalance(sessionId) {
  return request("/api/v1/credits/balance", { sessionId });
}

export async function fetchCreditPacks(sessionId) {
  return request("/api/v1/credits/packs?clientChannel=web", { sessionId });
}

export async function initializeCreditCheckout(sessionId, { packId, returnUrl }) {
  return request("/api/v1/credits/checkout/init", {
		body: JSON.stringify({ clientChannel: "web", packId, provider: "squad", returnUrl }),
		headers: { "Idempotency-Key": paymentIdempotencyKey() },
    method: "POST",
    sessionId,
  });
}

export async function confirmCreditCheckout(sessionId, reference) {
  return request("/api/v1/credits/checkout/confirm", {
    body: JSON.stringify({ provider: "squad", reference }),
    method: "POST",
    sessionId,
  });
}

export async function fetchExpenses(sessionId, filters = {}) {
  const params = new URLSearchParams({ limit: String(filters.limit ?? 500) });
  if (filters.from) params.set("from", filters.from);
  if (filters.to) params.set("to", filters.to);
  return request(`/api/v1/expenses?${params.toString()}`, { sessionId });
}

export async function createExpense(sessionId, expense) {
  return request("/api/v1/expenses", {
    body: JSON.stringify({ expense }),
    method: "POST",
    sessionId,
  });
}

export async function updateExpense(sessionId, expense) {
  return request(`/api/v1/expenses/${encodeURIComponent(expense.id)}`, {
    body: JSON.stringify({ expense }),
    method: "PATCH",
    sessionId,
  });
}

export async function deleteExpense(sessionId, expenseId) {
  return request(`/api/v1/expenses/${encodeURIComponent(expenseId)}`, {
    method: "DELETE",
    sessionId,
  });
}

export async function generateProductImage(sessionId, productId, payload = {}) {
  return request(`/api/v1/products/shop/${productId}/generate-image`, {
    body: JSON.stringify(payload),
    method: "POST",
    sessionId,
    timeoutMs: 95_000,
  });
}

export async function streamProductImageEvents(sessionId, options = {}) {
  return streamSSE("/api/v1/product-image-generations/events", {
    ...options,
    sessionId,
  });
}

export async function cleanProductImage(sessionId, productId, image, description = "") {
  if (!image || image.size > 1024 * 1024) {
    throw new Error("Product image must be 1 MB or smaller");
  }
  const body = new FormData();
  body.append("image", image);
  if (description.trim()) {
    body.append("description", description.trim());
  }
  return request(`/api/v1/products/shop/${productId}/clean-image`, {
    body,
    method: "POST",
    sessionId,
    timeoutMs: 95_000,
  });
}

export async function createProductCatalogCandidate(sessionId, payload) {
  return request("/api/v1/products/catalog/candidates", {
    body: JSON.stringify(payload),
    method: "POST",
    sessionId,
  });
}

export async function upsertShopProduct(sessionId, product, idempotencyKey) {
  return request("/api/v1/products/shop/upsert", {
    body: JSON.stringify({ idempotencyKey, product }),
    method: "POST",
    sessionId,
  });
}

export async function recordStockMovement(sessionId, movement, idempotencyKey) {
  return request("/api/v1/stock/movements", {
    body: JSON.stringify({ idempotencyKey, movement }),
    method: "POST",
    sessionId,
  });
}

export async function recordSale(sessionId, sale, idempotencyKey) {
  return request("/api/v1/sales", {
    body: JSON.stringify({ idempotencyKey, sale }),
    method: "POST",
    sessionId,
  });
}

export async function recordDebt(sessionId, debt, idempotencyKey) {
  return request("/api/v1/debts", {
    body: JSON.stringify({ idempotencyKey, debt }),
    method: "POST",
    sessionId,
  });
}

export async function recordDebtPayment(sessionId, payment, idempotencyKey) {
  return request("/api/v1/debts/payments", {
    body: JSON.stringify({ idempotencyKey, payment }),
    method: "POST",
    sessionId,
  });
}

export async function syncOutboundOperationsBatch(sessionId, operations, clientBatchId = "") {
  return request("/api/v1/sync/batch", {
    body: JSON.stringify({ clientBatchId, operations, platform: "web" }),
    method: "POST",
    sessionId,
  });
}

export async function reportSyncTelemetry(sessionId, telemetry) {
  return request("/api/v1/sync/telemetry", {
    body: JSON.stringify({
      ...telemetry,
      platform: "web",
    }),
    method: "POST",
    sessionId,
  });
}

export async function acknowledgeSyncCommand(sessionId, commandId, input) {
  return request(`/api/v1/sync/commands/${encodeURIComponent(commandId)}/ack`, {
    body: JSON.stringify(input),
    method: "POST",
    sessionId,
  });
}

export async function fetchBranches(sessionId) {
  return request("/api/v1/branches", { sessionId });
}

export async function selectSessionBranch(sessionId, branchId) {
  return request("/api/v1/auth/select-branch", {
    body: JSON.stringify({ branchId }),
    method: "POST",
    sessionId,
  });
}

export async function createBranch(sessionId, payload) {
  return request("/api/v1/branches", {
    body: JSON.stringify(payload),
    method: "POST",
    sessionId,
  });
}

export async function updateBranch(sessionId, branchId, payload) {
  return request(`/api/v1/branches/${branchId}`, {
    body: JSON.stringify(payload),
    method: "PATCH",
    sessionId,
  });
}

export async function deleteBranch(sessionId, branchId) {
  return request(`/api/v1/branches/${branchId}`, {
    method: "DELETE",
    sessionId,
  });
}

export async function fetchStaff(sessionId) {
  return request("/api/v1/staff", { sessionId });
}

export async function inviteStaff(sessionId, payload) {
  return request("/api/v1/staff", {
    body: JSON.stringify(payload),
    method: "POST",
    sessionId,
  });
}

export async function updateStaff(sessionId, staffId, payload) {
  return request(`/api/v1/staff/${staffId}`, {
    body: JSON.stringify(payload),
    method: "PATCH",
    sessionId,
  });
}

export async function deleteStaff(sessionId, staffId) {
  return request(`/api/v1/staff/${staffId}`, {
    method: "DELETE",
    sessionId,
  });
}

export async function fetchNotificationInbox(sessionId, filters = {}) {
  const params = new URLSearchParams({ limit: String(filters.limit ?? 40) });
  if (filters.category && filters.category !== "all") params.set("category", filters.category);
  if (filters.cursor) params.set("cursor", filters.cursor);
  return request(`/api/v1/notifications/inbox?${params.toString()}`, { sessionId });
}

export async function fetchNotificationInboxDetail(sessionId, notificationId) {
  return request(`/api/v1/notifications/inbox/${encodeURIComponent(notificationId)}`, { sessionId });
}

export async function markNotificationInboxRead(sessionId, notificationId) {
  return request(`/api/v1/notifications/inbox/${encodeURIComponent(notificationId)}/read`, {
    method: "PATCH",
    sessionId,
  });
}

export async function fetchNotificationDevices(sessionId) {
  return request("/api/v1/notifications/devices", { sessionId });
}

export async function fetchNotificationPreferences(sessionId) {
  const payload = await request("/api/v1/notifications/preferences", { sessionId });
  const preferences = payload.preferences || {};
  const labels = {
    billing: ["Billing & subscription", "Receipts, renewals, payment and plan alerts"],
    daily_summary: ["Daily summary", "A short sales summary the next morning"],
    low_stock: ["Low stock alerts", "Products that are low or out of stock"],
    weekly_insights: ["Weekly insights", "Weekly performance highlights and report"],
    monthly_reports: ["Monthly reports", "Your monthly business report and PDF"],
    analytics_alerts: ["Analytics alerts", "Important trends and business insights"],
  };
  const legacyEnabled = {
    billing: Boolean(preferences.billingEmail ?? preferences.billingPush ?? true),
    daily_summary: preferences.dailySummary ?? true,
    low_stock: preferences.lowStock ?? true,
    weekly_insights: preferences.weeklyInsights ?? true,
    monthly_reports: preferences.monthlyReports ?? true,
    analytics_alerts: preferences.weeklyInsights ?? true,
  };
  const routes = preferences.routes?.length ? preferences.routes : Object.keys(labels).map((eventType) => ({ eventType, enabled: legacyEnabled[eventType], email: true, telegram: false }));
  return routes.map((route) => ({
	  description: labels[route.eventType]?.[1] || route.eventType,
	  email: Boolean(route.email),
	  enabled: Boolean(route.enabled),
    id: route.eventType,
    label: labels[route.eventType]?.[0] || route.eventType,
    pushAvailable: preferences.pushAvailable ?? false,
    telegram: Boolean(route.telegram),
    telegramConnected: Boolean(preferences.telegramChatId),
  }));
}

export async function updateNotificationPreferences(sessionId, preferences) {
  return request("/api/v1/notifications/preferences", {
    body: JSON.stringify(preferences),
    method: "PATCH",
    sessionId,
  });
}

export async function fetchReportRecipients(sessionId) {
  const payload = await request("/api/v1/reports/recipients", { sessionId });
  return payload?.recipients || [];
}

export async function updateReportRecipient(sessionId, recipient) {
  const payload = await request(`/api/v1/reports/recipients/${encodeURIComponent(recipient.userId)}`, {
    body: JSON.stringify({
      dailyEnabled: Boolean(recipient.dailyEnabled),
      weeklyEnabled: Boolean(recipient.weeklyEnabled),
      monthlyEnabled: Boolean(recipient.monthlyEnabled),
    }),
    method: "PUT",
    sessionId,
  });
  return payload?.recipient;
}

export async function fetchTelegramStatus(sessionId) {
  return request("/api/v1/integrations/telegram/status", { sessionId });
}

export async function connectTelegram(sessionId) {
  return request("/api/v1/integrations/telegram/connect", { method: "POST", sessionId });
}

export async function disconnectTelegram(sessionId) {
  return request("/api/v1/integrations/telegram/connect", { method: "DELETE", sessionId });
}

export async function fetchTelegramMessages(sessionId) {
  return request("/api/v1/integrations/telegram/messages", { sessionId });
}

export async function sendTelegramTestMessage(sessionId, text) {
  if (typeof crypto === "undefined" || !crypto.randomUUID) {
    throw new Error("Secure random values are unavailable in this browser.");
  }
  return request("/api/v1/integrations/telegram/messages/text", {
    body: JSON.stringify({ idempotencyKey: crypto.randomUUID(), text }),
    method: "POST",
    sessionId,
  });
}

export async function createOnDemandReport(sessionId, input) {
  if (typeof crypto === "undefined" || !crypto.randomUUID) {
    throw new Error("Secure random values are unavailable in this browser.");
  }
  return request("/api/v1/reports", {
    body: JSON.stringify({
      ...input,
      idempotencyKey: input.idempotencyKey || crypto.randomUUID(),
    }),
    method: "POST",
    sessionId,
  });
}

export async function fetchOnDemandReport(sessionId, reportId) {
  return request(`/api/v1/reports/${encodeURIComponent(reportId)}`, { sessionId });
}

export async function updateShopProfile(sessionId, payload) {
  return request("/api/v1/settings/profile", {
    body: JSON.stringify(payload),
    method: "PATCH",
    sessionId,
  });
}

export async function changeLoginPassword(sessionId, payload) {
  return request("/api/v1/auth/password", {
    body: JSON.stringify({
      currentPassword: payload.currentPin,
      newPassword: payload.newPin,
      confirmPassword: payload.confirmPin,
    }),
    method: "PATCH",
    sessionId,
  });
}

export async function deleteCurrentAccount(sessionId) {
  return request("/api/v1/me", {
    method: "DELETE",
    sessionId,
  });
}

export async function fetchAnalyticsOverview(sessionId) {
  return request("/api/v1/analytics/overview", { sessionId });
}

export async function fetchAnalyticsRestockPlan(sessionId) {
  return request("/api/v1/analytics/restock-plan", { sessionId });
}

export async function fetchAnalyticsRestockPlanPage(sessionId, { limit = 40, offset = 0 } = {}) {
  const params = new URLSearchParams();
  params.set("limit", String(limit));
  params.set("offset", String(offset));
  return request(`/api/v1/analytics/restock-plan/list?${params.toString()}`, { sessionId });
}

export async function fetchAnalyticsTopPerformers(sessionId) {
  return request("/api/v1/analytics/top-performers", { sessionId });
}

export async function fetchAnalyticsTopPerformersPage(sessionId, { band, limit = 40, offset = 0 } = {}) {
  const params = new URLSearchParams();
  params.set("limit", String(limit));
  params.set("offset", String(offset));
  const path = band === "A" ? "a" : band === "B" ? "b" : "c";
  return request(`/api/v1/analytics/top-performers/${path}?${params.toString()}`, { sessionId });
}

export async function fetchAnalyticsTurnover(sessionId) {
  return request("/api/v1/analytics/turnover", { sessionId });
}

export async function fetchAnalyticsTurnoverMoversPage(sessionId, { kind, limit = 40, offset = 0 } = {}) {
  const params = new URLSearchParams();
  params.set("limit", String(limit));
  params.set("offset", String(offset));
  const path = kind === "slow" ? "slow-movers" : "fast-movers";
  return request(`/api/v1/analytics/turnover/${path}?${params.toString()}`, { sessionId });
}

export async function fetchAnalyticsPatterns(sessionId) {
  return request("/api/v1/analytics/customer-buying-patterns", { sessionId });
}

export async function fetchAnalyticsPatternsPage(sessionId, { limit = 40, offset = 0 } = {}) {
  const params = new URLSearchParams();
  params.set("limit", String(limit));
  params.set("offset", String(offset));
  return request(`/api/v1/analytics/customer-buying-patterns/list?${params.toString()}`, { sessionId });
}

export async function fetchAnalyticsDemandForecast(sessionId) {
  return request("/api/v1/analytics/demand-forecast", { sessionId });
}

export async function fetchAnalyticsDemandForecastPage(sessionId, { band, limit = 40, offset = 0 } = {}) {
  const params = new URLSearchParams();
  params.set("limit", String(limit));
  params.set("offset", String(offset));
  return request(`/api/v1/analytics/demand-forecast/${band}?${params.toString()}`, { sessionId });
}

export async function fetchAnalyticsConflicts(sessionId) {
  return request("/api/v1/analytics/inventory-conflicts", { sessionId });
}

export async function fetchAnalyticsAlerts(sessionId) {
  return request("/api/v1/analytics/alerts", { sessionId });
}

export async function fetchAssistantReply(sessionId, payload) {
  return request("/api/v1/assistant/reply", {
    body: JSON.stringify(payload),
    method: "POST",
    sessionId,
  });
}

export async function fetchAssistantMessages(sessionId, cursor = "", limit = 30) {
  const params = new URLSearchParams({ limit: String(Math.min(30, Math.max(1, limit))) });
  if (cursor) params.set("cursor", cursor);
  return request(`/api/v1/assistant/messages?${params.toString()}`, { sessionId });
}
